import React from 'react'

import {getColliTypeName} from '../../utils/getColliTypeName.js'

import {Button, Colors, Icon, Modal, P, Row, S1, S2} from '../../src/components/UI/index.js'
import {formSpacing, Input, RadioButton} from '../../src/components/UI/forms/index.js'
import {Collo, Reseller} from 'types/types.js'
import t from 'src/helpers/Languages.js'

type ColliType = Reseller['settings']['orders']['colliTypes'][number]

export type LinkBarcodeParams = {
    /** the scanned barcode */
    barcode: string
    /** the colli of this address without a barcode */
    colli: Collo[]
    /** the colli types this customer is allowed to use */
    colliTypes: ColliType[]
    /** the number a new collo would get */
    nextColloNumber: number
    /** skip the choice and link to the only collo of this address */
    linkDirectly?: boolean
    /** shown above the details with the title Let op!, e.g. when the barcode is added as a new collo right away */
    warning?: string
}

export type LinkBarcodeResult = {
    /** the collo to link the barcode to, null when a new collo should be added */
    collo: Collo | null
    type: string
    description: string
}

type LinkBarcodePopupState = {
    showPopup: boolean
    step: 'choose' | 'details'
    barcode: string
    colli: Collo[]
    colliTypes: ColliType[]
    nextColloNumber: number
    collo: Collo | null
    type: string
    description: string
    warning: string
    resolve: ((result: LinkBarcodeResult | null) => void) | null
}

export default class LinkBarcodePopup extends React.Component<Record<string, never>, LinkBarcodePopupState> {
    constructor(props) {
        super(props)

        this.state = {
            showPopup: false,
            step: 'choose',
            barcode: '',
            colli: [],
            colliTypes: [],
            nextColloNumber: 1,
            collo: null,
            type: '',
            description: '',
            warning: '',
            resolve: null
        }
    }

    /**
     * Opens at adding a new collo when there are no colli without barcode. Resolves with null when the courier cancels.
     */
    open({barcode, colli, colliTypes, nextColloNumber, linkDirectly, warning}: LinkBarcodeParams): Promise<LinkBarcodeResult | null> {
        return new Promise((resolve) => {
            this.setState({
                showPopup: true,
                step: linkDirectly || !colli.length ? 'details' : 'choose',
                barcode,
                colli,
                colliTypes,
                nextColloNumber,
                collo: linkDirectly ? colli[0] : null,
                type: linkDirectly ? (colli[0].type || '') : '',
                description: linkDirectly ? (colli[0].description || '') : '',
                warning: warning || '',
                resolve
            })
        })
    }

    close(result: LinkBarcodeResult | null) {
        const {resolve} = this.state

        this.setState({showPopup: false, resolve: null})

        resolve?.(result)
    }

    colloDetails(collo: Partial<Collo>) {
        const {colliTypes} = this.state
        const parts: string[] = []

        if (collo.type) {
            parts.push(getColliTypeName(collo.type, colliTypes))
        }

        if (collo.weight) {
            parts.push(`${collo.weight / 1000} kg`)
        }

        if (collo.length || collo.width || collo.height) {
            parts.push(`${collo.length || 0} x ${collo.width || 0} x ${collo.height || 0} mm`)
        }

        return parts.join(' - ')
    }

    renderColloCard(collo: Partial<Collo>, number: number, barcode: string, onClick?: () => void) {
        const details = this.colloDetails(collo)

        return (
            <Row
                style={{flex: 1, paddingLeft: 6, paddingRight: 6,
                    flexDirection: 'column',
                    background: Colors.backgroundWhite,
                    borderRadius: 4,
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                    padding: '3px 12px',
                    marginBottom: 24,
                    cursor: onClick ? 'pointer' : 'default', minWidth: 'auto'
                }}
                onClick={onClick}
            >
                <Row style={{alignItems: 'center'}}>
                    <P style={{width: 20, marginRight: 6, fontWeight: 700, textAlign: 'center'}}>{number}</P>

                    <P ellipsis style={{flex: 1}}>{barcode || t('linkBarcodeNoBarcode')}</P>

                    <Row style={{width: 'unset', marginLeft: 'auto', alignItems: 'center'}}>
                        {!!barcode &&
                            <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-barcode-scan' />
                        }

                        <Icon style={{fontSize: 12, marginRight: 0, color: Colors.successBright}} icon='mdi mdi-circle' />
                    </Row>
                </Row>

                {!!details &&
                    <Row>
                        <P ellipsis style={{marginLeft: 26}}>{details}</P>
                    </Row>
                }

                {!!collo.description &&
                    <Row style={{alignItems: 'center'}}>
                        <Icon style={{marginRight: 6, marginLeft: 26}} size='md' icon='mdi mdi-message-outline' />

                        <P style={{flex: 1}}>{collo.description}</P>
                    </Row>
                }
            </Row>
        )
    }

    render() {
        const {showPopup, step, barcode, colli, colliTypes, nextColloNumber, collo, type, description, warning} = this.state

        const titleStyle: React.CSSProperties = {fontWeight: 700, color: Colors.textDark}
        const labelStyle: React.CSSProperties = {fontWeight: 700, letterSpacing: 0.5, color: Colors.textDark, marginBottom: 12}

        return (
            <Modal
                show={showPopup}
                style={{width: '90%'}}
                onClose={() => this.close(null)}
                closeOnBackdropClick
            >
                {step === 'choose' ?
                    <>
                        <Row style={{alignItems: 'center', marginBottom: 24}}>
                            <Icon style={{marginRight: 6}} size='lg' icon='mdi mdi-barcode-scan' />

                            <S1 style={titleStyle}>{t('linkBarcodeNewBarcode')}</S1>
                        </Row>

                        <P style={{marginBottom: 24}}>{t('linkBarcodeChooseText')}</P>

                        <S2 style={labelStyle}>{t('linkBarcodeToKnownCollo')}</S2>

                        {colli.map((colloWithoutBarcode, index) => (
                            <React.Fragment key={`Collo${colloWithoutBarcode.index ?? index}`}>
                                {this.renderColloCard(
                                    colloWithoutBarcode,
                                    (colloWithoutBarcode.index || 0) + 1,
                                    '',
                                    () => this.close({collo: colloWithoutBarcode, type: colloWithoutBarcode.type || '', description: colloWithoutBarcode.description || ''})
                                )}
                            </React.Fragment>
                        ))}

                        <Button
                            style={{flex: 1, marginTop: 24}}
                            variant='solid'
                            icon='mdi mdi-plus'
                            label={t('linkBarcodeAddNewCollo')}
                            onClick={() => this.setState({step: 'details', collo: null, type: '', description: ''})}
                        />
                    </> :
                    <>
                        <Row style={{alignItems: 'center', marginBottom: 24}}>
                            <Icon style={{marginRight: 6}} size='lg' icon={warning ? 'mdi mdi-alert' : 'mdi mdi-barcode-scan'} />

                            <S1 style={titleStyle}>{warning ? t('linkBarcodeWarningTitle') : collo ? t('linkBarcodeLink') : t('linkBarcodeAddCollo')}</S1>
                        </Row>

                        {!!warning &&
                            <P style={{marginBottom: 24}}>{warning}</P>
                        }

                        {this.renderColloCard(
                            collo || {},
                            collo ? (collo.index || 0) + 1 : nextColloNumber,
                            barcode
                        )}

                        {colliTypes.length > 0 &&
                            <S2 style={{...labelStyle, marginTop: 24}}>{t('linkBarcodeColliType')}</S2>
                        }

                        {colliTypes.map((colliType) => (
                            <RadioButton
                                key={colliType.id}
                                style={{height: 'auto', minHeight: 36, marginBottom: 24}}
                                clickableLabel
                                unsetWhiteSpace
                                value={colliType.id}
                                label={colliType.name}
                                checked={type === colliType.id}
                                onChange={(event: {target: {value: string}}) => this.setState({type: type === event.target.value ? '' : event.target.value})}
                            />
                        ))}

                        <Input
                            style={{marginTop: 12}}
                            label={t('linkBarcodeDescription')}
                            value={description}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({description: event.target.value})}
                        />

                        <Row style={{marginTop: 24, justifyContent: 'flex-end', gap: formSpacing.section}}>
                            <Button variant='text' onClick={() => this.close(null)} label={t('cancel')} />

                            <Button variant='text' onClick={() => this.close({collo, type, description})} label={t('confirm')} />
                        </Row>
                    </>
                }
            </Modal>
        )
    }
}
